/**
 * Reactive `matchMedia` binding.
 *
 * {@link mediaQuery$} returns an observable that tracks whether a CSS media
 * query currently matches and re-emits when it flips (viewport resize,
 * orientation change). Use it when a JS decision must stay in lockstep with a
 * CSS `@media` breakpoint — e.g. requesting a different CDN image crop for a
 * layout that the stylesheet also switches at the same width — so the two can't
 * drift apart.
 *
 * Observables are memoized per query string, so every caller of a given query
 * shares ONE `MediaQueryList` listener; the listener lives for the page's
 * lifetime (matching {@link reduceMotion$}), which is why callers must not pass
 * dynamically-generated query strings. Guarded against non-browser hosts (SSR,
 * vitest/linkedom, older WebViews) — a missing `matchMedia` yields a static
 * `false` observable.
 */

import { data, type ObservableValue } from '@amazon/vinyl-observable'

const cache = new Map<string, ObservableValue<boolean>>()

function getMatchMedia(): ((q: string) => MediaQueryList) | null {
    if (typeof window === 'undefined') return null
    const mm = (window as { matchMedia?: (q: string) => MediaQueryList }).matchMedia
    return typeof mm === 'function' ? mm.bind(window) : null
}

export function mediaQuery$(query: string): ObservableValue<boolean> {
    const cached = cache.get(query)
    if (cached) return cached

    const mm = getMatchMedia()
    let mql: MediaQueryList | null = null
    try {
        mql = mm?.(query) ?? null
    } catch {
        // Older WebViews throw on queries they can't parse — degrade to `false`.
        mql = null
    }

    const state$ = data(mql?.matches ?? false)
    mql?.addEventListener('change', (e) => {
        state$.value = e.matches
    })
    cache.set(query, state$)
    return state$
}
